import React, { Component, Suspense, lazy } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Switch, Redirect } from 'react-router-dom';
import { withRouter } from 'react-router';
import { bool, func, object } from 'prop-types';
import queryString from 'query-string';

import { get, isEmpty } from 'ravenjs/utils/lodash';

import { APP_PERMISSIONS } from 'constants/permissions';
import { actions as inProductMarketingActions } from 'modules/inProductMarketing';
import { selectors as authSelectors } from 'modules/auth';
import { selectors as userSelectors } from 'modules/user';
import { buildPayloadForInProductMarketing } from 'utils/common';
import { getOnClickBackCallback, hasSearch, pushesToSearchPage } from 'utils/search';
import ContentSearch from 'components/ContentSearch';

import { getHotjarApiKey, loadHotjar } from 'utils/hotjar';
import { EXCLUDE_PARTNER_ID_HOTJAR } from 'constants/app';
import Route from './Route';

// Lazily load in all of the routes.
const Admin = lazy(() => import('../containers/Admin'));
const Auth = lazy(() => import('../containers/Auth'));
const Dashboard = lazy(() => import('../containers/Dashboard'));
const HRTools = lazy(() => import('../containers/HRTools'));
const Impersonation = lazy(() => import('../containers/Impersonation'));
const LegalAndPrivacy = lazy(() => import('../containers/LegalAndPrivacy'));
const Profile = lazy(() => import('../containers/Profile'));
const Compliance = lazy(() => import('../containers/Compliance'));
const MineralIntelligence = lazy(() => import('../containers/MineralIntelligence'));
const MyTickets = lazy(() => import('../containers/MyTickets'));
const Ticketing = lazy(() => import('../containers/Ticketing'));
const Tools = lazy(() => import('../containers/Tools'));
const Reporting = lazy(() => import('../containers/Reporting'));
const Search = lazy(() => import('../containers/Search'));
const FutureWork = lazy(() => import('../containers/FutureWork'));
const Templates = lazy(() => import('../containers/Templates'));
const Notifications = lazy(() => import('../containers/Notifications'));
const CompanyPolicies = lazy(() => import('../containers/CompanyPolicies'));
const Document = lazy(() => import('../containers/Document'));
const Public = lazy(() => import('../containers/Public'));
const MindmatrixSSO = lazy(() => import('../containers/MindmatrixSSO'));
const Prospect = lazy(() => import('../containers/Prospect'));
const Resources = lazy(() => import('../containers/Resources'));
const Safety = lazy(() => import('../containers/Safety'));
const TodoList = lazy(() => import('../containers/TodoList'));
const Training = lazy(() => import('../containers/Training'));
const HelpCenter = lazy(() => import('../containers/HelpCenter'));
const CaseCave = lazy(() => import('../containers/CaseCave'));
const OpenIdConnect = lazy(() => import('../containers/OpenIdConnect'));
const OpenIdRedirect = lazy(() => import('../containers/OpenIdRedirect'));

class Routes extends Component {
    constructor(props) {
        super(props);
        const {
            loggedUser: { company },
        } = this.props;

        const hotjarApiKey = getHotjarApiKey();
        const excludePartnerId = EXCLUDE_PARTNER_ID_HOTJAR.indexOf(company.partnerId);

        if (hotjarApiKey && company.partnerId && excludePartnerId === -1) {
            loadHotjar(hotjarApiKey);
        }

        this.routeListener(props.history);
    }

    componentDidMount = () => {};

    setTicketingIframeRef = input => {
        this.ticketingiframeRef = input;
    };

    sendEmail = async (pendoId, optyName, source, adType) => {
        const { isImpersonatingUser, loggedUser, sendEmail } = this.props;
        try {
            const params = buildPayloadForInProductMarketing(
                loggedUser,
                isImpersonatingUser,
                optyName,
                pendoId.replace('&', ''),
                source,
                adType
            );
            await sendEmail({ params });
            pendo.showGuideByName('In-Product Marketing - Lead Success');
        } catch (e) {
            pendo.showGuideByName('In-Product Marketing - Lead Failure');
        }
    };

    routeListener = history => {
        history.listen(location => {
            const locationHash = get(history, 'location.hash', '');
            if (location.pathname.includes('/ticketing')) {
                this.ticketingiframeRef &&
                    this.ticketingiframeRef.sendMessage({ route: location.pathname }, ['*']);
            } else if (!isEmpty(locationHash) && locationHash.includes('OptyName')) {
                const guideId = locationHash.split('OptyName=');
                const queryParamsParse = queryString.parse(locationHash);
                const source = queryParamsParse ? queryParamsParse.Source : '';
                const adType = queryParamsParse ? queryParamsParse.AdType : '';
                const optyName = queryParamsParse ? queryParamsParse.OptyName : '';

                history.push({
                    location: {
                        hash: '',
                    },
                });

                this.sendEmail(get(guideId, '0', ''), optyName, source, adType);
            }
        });
    };

    render() {
        const { history } = this.props;
        const p = get(history, 'location.pathname', null);

        return (
            <Suspense fallback={<div />}>
                {hasSearch(p) && (
                    <ContentSearch
                        enablePushToSearchPage={pushesToSearchPage(p)}
                        onClickBack={getOnClickBackCallback(history)}
                    />
                )}
                <Switch>
                    <Route path="/auth" render={props => <Auth {...props} />} isPublic />
                    <Route
                        path="/openid/redirect"
                        exact
                        render={props => <OpenIdRedirect {...props} />}
                        isPublic
                    />
                    <Route path="/openid" render={props => <OpenIdConnect {...props} />} isPublic />
                    <Route path="/dashboard" render={props => <Dashboard {...props} />} />
                    <Route path="/help-center" render={props => <HelpCenter {...props} />} />
                    <Route path="/hr-tools" render={props => <HRTools {...props} />} />
                    <Route
                        path="/partner-enablement"
                        permissions={APP_PERMISSIONS.resources}
                        render={props => <MindmatrixSSO {...props} />}
                    />
                    <Route path="/profile" component={Profile} />
                    <Route
                        path="/admin"
                        permissions={APP_PERMISSIONS.system}
                        render={props => <Admin {...props} />}
                    />
                    <Route path="/legal-and-privacy" component={LegalAndPrivacy} />
                    <Route
                        path="/training/:section/category/:categoryId/:categoryName/:subcategoryId/:subcategory"
                        render={props => <Training {...props} />}
                    />
                    <Route
                        path="/training/:section/category/:categoryId/:categoryName"
                        render={props => <Training {...props} />}
                    />
                    <Route
                        path="/training/:section/:sectionId/:extraSectionId"
                        render={props => <Training {...props} />}
                    />
                    <Route
                        path="/training/:section/:sectionId"
                        render={props => <Training {...props} />}
                    />
                    <Route path="/training/:section" render={props => <Training {...props} />} />
                    <Route
                        path="/impersonation/:action"
                        render={props => <Impersonation {...props} />}
                    />
                    <Route
                        path="/hr-compliance"
                        permissions={APP_PERMISSIONS.compliance}
                        render={props => <Compliance {...props} />}
                    />
                    <Route
                        path="/featured-content/:id/details"
                        permissions={APP_PERMISSIONS.compliance}
                        render={props => <Document {...props} />}
                    />
                    <Route
                        path="/mineral-intelligence"
                        render={props => <MineralIntelligence {...props} />}
                    />
                    <Route
                        path="/ticketing"
                        permissions={APP_PERMISSIONS.systemTicketingSystemView}
                        render={props => (
                            <Ticketing
                                {...props}
                                setTicketingIframeRef={this.setTicketingIframeRef}
                            />
                        )}
                    />
                    <Route
                        path="/case-cave"
                        permissions={APP_PERMISSIONS.systemTicketingSystemView}
                        render={props => {
                            const { history, location } = props;
                            if (
                                location &&
                                location.pathname.includes('/case-cave/prepped_answers')
                            ) {
                                const newPath = location.pathname.replace(
                                    '/case-cave',
                                    '/ticketing'
                                );
                                history.replace(newPath);
                                return null;
                            }

                            return (
                                <CaseCave
                                    {...props}
                                    setTicketingIframeRef={this.setTicketingIframeRef}
                                />
                            );
                        }}
                    />
                    <Route path="/my-cases" render={props => <MyTickets {...props} />} />
                    <Route
                        path="/my-tickets"
                        render={props => {
                            // Redirecting legacy my-tickets url to my-cases,
                            // please refer GN-7395 for more details.
                            const { history, location } = props;
                            if (location && location.pathname.includes('/my-tickets')) {
                                const newPath = location.pathname.replace(
                                    '/my-tickets',
                                    '/my-cases'
                                );
                                history.replace(newPath);
                            }
                            return null;
                        }}
                    />
                    <Route path="/tools" render={props => <Tools {...props} />} />
                    <Route path="/reporting" render={props => <Reporting {...props} />} />
                    <Route
                        path="/search"
                        permissions={APP_PERMISSIONS.compliance}
                        render={props => <Search {...props} />}
                    />
                    <Route path="/futurework" render={props => <FutureWork {...props} />} />
                    <Route path="/templates" render={props => <Templates {...props} />} />
                    <Route path="/resources" render={props => <Resources {...props} />} />
                    <Route path="/notifications" render={props => <Notifications {...props} />} />
                    <Route
                        path="/company-policies"
                        render={props => <CompanyPolicies {...props} />}
                    />
                    <Route path="/prospect" render={props => <Prospect {...props} />} />
                    <Route path="/safety" render={props => <Safety {...props} />} />
                    <Route path="/todo" render={props => <TodoList {...props} />} />
                    <Route isPublic path="/public" render={props => <Public {...props} />} />
                    <Redirect from="/login" to="/auth/login" />
                    <Redirect from="/logout" to="/auth/logout" />
                    {/* Redirect all unknown routes to the `dashboard` for now */}
                    <Redirect to="/dashboard" />
                </Switch>
            </Suspense>
        );
    }
}

Routes.propTypes = {
    history: object,
    isImpersonatingUser: bool.isRequired,
    loggedUser: object,
    sendEmail: func.isRequired,
};

Routes.defaultProps = {
    history: {},
    loggedUser: {},
};

const mapDispatchToProps = {
    sendEmail: inProductMarketingActions.sendEmail,
};

const mapStateToProps = createStructuredSelector({
    isImpersonatingUser: authSelectors.isImpersonatingUser,
    loggedUser: userSelectors.getUser,
});

export { Routes as RoutesUnwrapped };
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Routes));
